/**
 * CategoryEditor Component
 *
 * Manages categories and items within an inspection template.
 * Supports drag-and-drop reordering.
 */

import { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  TextField,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  FormControlLabel,
  Stack,
  Divider,
  Tooltip,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import StraightenIcon from '@mui/icons-material/Straighten';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import GridViewIcon from '@mui/icons-material/GridView';
import type { Category, CategoryItem, MeasurementDefinition, SubItemDefinition } from '@/hooks/useInspections';
import { useTranslation } from 'react-i18next';
import { MeasurementsEditor } from './MeasurementsEditor';
import { SubItemsEditor } from './SubItemsEditor';

interface CategoryEditorProps {
  categories: Category[];
  onChange: (categories: Category[]) => void;
}

function generateId(): string {
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

interface ItemEditorProps {
  item: CategoryItem;
  onSave: (item: CategoryItem) => void;
  onCancel: () => void;
}

function ItemEditor({ item, onSave, onCancel }: ItemEditorProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description ?? '');
  const [requiresPhoto, setRequiresPhoto] = useState(item.requiresPhoto ?? false);
  const [hasMeasurement, setHasMeasurement] = useState(item.hasMeasurement ?? false);
  const [measurementUnit, setMeasurementUnit] = useState(item.measurementUnit ?? '');
  const [measurements, setMeasurements] = useState<MeasurementDefinition[]>(item.measurements ?? []);
  const [subItems, setSubItems] = useState<SubItemDefinition[]>(item.subItems ?? []);

  const hasMultiMeasurements = measurements.length > 0 || subItems.length > 0;

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({
      ...item,
      name: name.trim(),
      description: description.trim() || undefined,
      requiresPhoto,
      hasMeasurement: hasMultiMeasurements ? false : hasMeasurement,
      measurementUnit: hasMultiMeasurements ? undefined : (hasMeasurement ? measurementUnit.trim() || undefined : undefined),
      measurements: measurements.length > 0 ? measurements.filter((m) => m.label.trim() && m.unit.trim()) : undefined,
      subItems: subItems.length > 0 ? subItems.filter((s) => s.label.trim()) : undefined,
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'action.hover' }}>
      <Stack spacing={2}>
        <TextField
          label={t('inspectionTemplates.itemName')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          fullWidth
          autoFocus
        />
        <TextField
          label={t('inspectionTemplates.itemDescription')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          size="small"
          fullWidth
          multiline
          rows={2}
        />
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControlLabel
            control={
              <Checkbox
                checked={requiresPhoto}
                onChange={(e) => setRequiresPhoto(e.target.checked)}
                size="small"
              />
            }
            label={t('inspectionTemplates.requiresPhoto')}
          />
          {!hasMultiMeasurements && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasMeasurement}
                    onChange={(e) => setHasMeasurement(e.target.checked)}
                    size="small"
                  />
                }
                label={t('inspectionTemplates.measurement')}
              />
              {hasMeasurement && (
                <TextField
                  label={t('inspectionTemplates.measurementUnit')}
                  value={measurementUnit}
                  onChange={(e) => setMeasurementUnit(e.target.value)}
                  size="small"
                  sx={{ width: 100 }}
                  placeholder={t('inspectionTemplates.measurementUnitPlaceholder')}
                />
              )}
            </>
          )}
        </Box>

        <Divider />

        <SubItemsEditor subItems={subItems} onChange={setSubItems} />
        <MeasurementsEditor measurements={measurements} onChange={setMeasurements} />

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button size="small" onClick={onCancel} startIcon={<CloseIcon />}>
            {tc('cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            startIcon={<SaveIcon />}
            disabled={!name.trim()}
          >
            {tc('save')}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}

interface CategoryCardProps {
  category: Category;
  onUpdate: (category: Category) => void;
  onDelete: () => void;
}

function CategoryCard({ category, onUpdate, onDelete }: CategoryCardProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const [expanded, setExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [name, setName] = useState(category.name);
  const [description, setDescription] = useState(category.description ?? '');

  const handleSaveCategory = () => {
    if (!name.trim()) return;
    onUpdate({
      ...category,
      name: name.trim(),
      description: description.trim() || undefined,
    });
    setIsEditing(false);
  };

  const handleAddItem = () => {
    const newItem: CategoryItem = {
      id: generateId(),
      name: '',
      sortOrder: category.items.length,
    };
    onUpdate({
      ...category,
      items: [...category.items, newItem],
    });
    setEditingItemId(newItem.id);
  };

  const handleSaveItem = (item: CategoryItem) => {
    onUpdate({
      ...category,
      items: category.items.map((i) => (i.id === item.id ? item : i)),
    });
    setEditingItemId(null);
  };

  const handleDeleteItem = (itemId: string) => {
    onUpdate({
      ...category,
      items: category.items
        .filter((i) => i.id !== itemId)
        .map((i, idx) => ({ ...i, sortOrder: idx })),
    });
  };

  const handleCancelItemEdit = () => {
    // If the item has no name, remove it (it was just added)
    const item = category.items.find((i) => i.id === editingItemId);
    if (item && !item.name) {
      handleDeleteItem(editingItemId!);
    }
    setEditingItemId(null);
  };

  const moveItem = (fromIndex: number, direction: 'up' | 'down') => {
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= category.items.length) return;

    const newItems = [...category.items];
    const removed = newItems.splice(fromIndex, 1)[0];
    if (!removed) return;
    newItems.splice(toIndex, 0, removed);

    onUpdate({
      ...category,
      items: newItems.map((i, idx) => ({ ...i, sortOrder: idx })),
    });
  };

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {/* Category header */}
      <Box
        sx={{
          p: 2,
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <DragIndicatorIcon color="action" sx={{ cursor: 'grab' }} />

        {isEditing ? (
          <Stack spacing={1} flex={1}>
            <TextField
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              label={t('inspectionTemplates.categoryName')}
              autoFocus
            />
            <TextField
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              size="small"
              label={t('inspectionTemplates.categoryDescription')}
            />
            <Box display="flex" gap={1}>
              <Button size="small" onClick={() => setIsEditing(false)}>
                {tc('cancel')}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSaveCategory}
                disabled={!name.trim()}
              >
                {tc('save')}
              </Button>
            </Box>
          </Stack>
        ) : (
          <>
            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                {category.name}
              </Typography>
              {category.description && (
                <Typography variant="body2" color="text.secondary">
                  {category.description}
                </Typography>
              )}
            </Box>

            <Chip
              size="small"
              label={t('inspectionTemplates.itemCount', { count: category.items.length })}
              variant="outlined"
            />

            <Tooltip title={t('inspectionTemplates.editCategory')}>
              <IconButton size="small" onClick={() => setIsEditing(true)} aria-label={t('inspectionTemplates.editCategory')} sx={{ minWidth: 44, minHeight: 44 }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('inspectionTemplates.deleteCategory')}>
              <IconButton size="small" onClick={onDelete} color="error" aria-label={t('inspectionTemplates.deleteCategory')} sx={{ minWidth: 44, minHeight: 44 }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={expanded ? tc('collapse') : tc('expand')}>
              <IconButton size="small" onClick={() => setExpanded(!expanded)} aria-label={expanded ? tc('collapse') : tc('expand')} sx={{ minWidth: 44, minHeight: 44 }}>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {/* Category items */}
      <Collapse in={expanded && !isEditing}>
        <Divider />
        <List dense disablePadding>
          {category.items
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((item, index) => (
              <Box key={item.id}>
                {editingItemId === item.id ? (
                  <Box sx={{ p: 1 }}>
                    <ItemEditor
                      item={item}
                      onSave={handleSaveItem}
                      onCancel={handleCancelItemEdit}
                    />
                  </Box>
                ) : (
                  <ListItem
                    sx={{
                      '&:hover': { bgcolor: 'action.hover' },
                      pl: 4,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      <Tooltip title={tc('moveUp')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={index === 0}
                            onClick={() => moveItem(index, 'up')}
                            sx={{ p: 0.25, minWidth: 44, minHeight: 44 }}
                            aria-label={tc('moveUp')}
                          >
                            <ExpandLessIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={tc('moveDown')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={index === category.items.length - 1}
                            onClick={() => moveItem(index, 'down')}
                            sx={{ p: 0.25, minWidth: 44, minHeight: 44 }}
                            aria-label={tc('moveDown')}
                          >
                            <ExpandMoreIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>

                    <ListItemText
                      primary={item.name}
                      secondary={item.description}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />

                    <Box display="flex" alignItems="center" gap={0.5} mr={1}>
                      {item.requiresPhoto && (
                        <Tooltip title={t('inspectionTemplates.requiresPhoto')}>
                          <PhotoCameraIcon fontSize="small" color="action" />
                        </Tooltip>
                      )}
                      {item.hasMeasurement && (
                        <Tooltip title={t('inspectionTemplates.measurementInfo', { unit: item.measurementUnit ?? '' })}>
                          <StraightenIcon fontSize="small" color="action" />
                        </Tooltip>
                      )}
                      {((item.measurements && item.measurements.length > 0) || (item.subItems && item.subItems.length > 0)) && (
                        <Tooltip title={t('inspectionTemplates.multiMeasurementInfo', {
                          subItems: item.subItems?.length ?? 0,
                          measurements: item.measurements?.length ?? 0,
                        })}>
                          <GridViewIcon fontSize="small" color="primary" />
                        </Tooltip>
                      )}
                    </Box>

                    <ListItemSecondaryAction>
                      <Tooltip title={tc('edit')}>
                        <IconButton
                          size="small"
                          onClick={() => setEditingItemId(item.id)}
                          aria-label={tc('edit')}
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={tc('delete')}>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteItem(item.id)}
                          color="error"
                          aria-label={tc('delete')}
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                )}
                {index < category.items.length - 1 && <Divider component="li" />}
              </Box>
            ))}
        </List>

        <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddItem}
            fullWidth
          >
            {t('inspectionTemplates.addItem')}
          </Button>
        </Box>
      </Collapse>
    </Paper>
  );
}

export function CategoryEditor({ categories, onChange }: CategoryEditorProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');

  const handleAddCategory = () => {
    const newCategory: Category = {
      id: generateId(),
      name: t('inspectionTemplates.newCategory'),
      sortOrder: categories.length,
      items: [],
    };
    onChange([...categories, newCategory]);
  };

  const handleUpdateCategory = (index: number, category: Category) => {
    const newCategories = [...categories];
    newCategories[index] = category;
    onChange(newCategories);
  };

  const handleDeleteCategory = (index: number) => {
    onChange(
      categories
        .filter((_, i) => i !== index)
        .map((c, idx) => ({ ...c, sortOrder: idx }))
    );
  };

  const moveCategory = (fromIndex: number, direction: 'up' | 'down') => {
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= categories.length) return;

    const newCategories = [...categories];
    const removed = newCategories.splice(fromIndex, 1)[0];
    if (!removed) return;
    newCategories.splice(toIndex, 0, removed);

    onChange(newCategories.map((c, idx) => ({ ...c, sortOrder: idx })));
  };

  return (
    <Stack spacing={2}>
      {categories
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((category, index) => (
          <Box key={category.id}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Tooltip title={tc('moveUp')}>
                <span>
                  <IconButton
                    size="small"
                    disabled={index === 0}
                    onClick={() => moveCategory(index, 'up')}
                    aria-label={tc('moveUp')}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    <ExpandLessIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={tc('moveDown')}>
                <span>
                  <IconButton
                    size="small"
                    disabled={index === categories.length - 1}
                    onClick={() => moveCategory(index, 'down')}
                    aria-label={tc('moveDown')}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                {t('inspectionTemplates.categoryDefault', { index: index + 1 })}
              </Typography>
            </Box>
            <CategoryCard
              category={category}
              onUpdate={(cat) => handleUpdateCategory(index, cat)}
              onDelete={() => handleDeleteCategory(index)}
            />
          </Box>
        ))}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddCategory}
        sx={{ alignSelf: 'flex-start' }}
      >
        {t('inspectionTemplates.addCategory')}
      </Button>
    </Stack>
  );
}
